/**
 * nodeAnatomy.js — Phase R1: per-task-type HTML/CSS spec.
 *
 * Conductor OSS renders every scalar task inside a 350px-wide rounded rectangle, but
 * the *height* and the *metadata shown inside* vary by task type per the
 * ORKES_DAG_RULESET §S-G codebook. Heights cluster into six discrete buckets:
 * {100, 110, 120, 130, 140, 150}. Everything outside those buckets is a bug.
 *
 * This module is the single source of truth for per-type visual anatomy:
 *
 *   • `getTaskHeight(nodeType, config)`   → bucketed height (100…150)
 *   • `getTaskMetadata(nodeType, config)` → { line, kind } | null  (third
 *                                             line rendered under label+sublabel)
 *
 * Both are pure functions of (nodeType, config). No DOM, no React, no async.
 * That lets them feed three consumers without coupling:
 *
 *   1. `irAdapter.layoutDAGviaIR` — seeds the initial heightMap so the
 *      first-pass IR layout positions containers using per-type heights
 *      instead of the flat NODE_H=100. Without this, every SWITCH/FORK
 *      frame is sized as if every child were a 100px SIMPLE task, which is
 *      exactly the "long painful DAG bug" we're finally closing.
 *
 *   2. `CanvasNode` (in CanvasPage.jsx) — sizes the SVG rect to the
 *      per-type height and renders the extra metadata line inside.
 *
 *   3. `useIRHeightFeedback` — after DOM measurement, any drift from the
 *      computed height re-runs the layout. Because `getTaskHeight` returns
 *      bucketed values already, a stable second pass is guaranteed.
 *
 * DESIGN: the height table is ADDITIVE. Unknown node types fall through to
 * the base 100 bucket (SIMPLE-equivalent). That preserves legacy behaviour
 * until we grow per-type rendering for every task. Metadata extraction is
 * likewise fall-through: types we don't recognise produce null, which the
 * renderer treats as "skip the third line" (no visual regression).
 */

// ────────────────────────────────────────────────────────────────
//  Height table — ORKES_DAG_RULESET §S-G
// ────────────────────────────────────────────────────────────────
//
// Each value MUST be one of {90, 100, 110, 120, 130, 140, 150} (Rule S-G.a).
// A value of 100 means "the type has no extra metadata to display beyond the
// default label+sublabel zone" — i.e. same as the legacy generic fallback.
// 90 is reserved for the TERMINATE display marker only (Conductor OSS parity: Conductor OSS
// renders TERMINATE as a 350×90 pill, 10px shorter than regular tasks).
// Higher values reserve vertical room for a single metadata line (110, 120)
// or a two-line preview block (130, 140, 150).

export const TASK_HEIGHT_BUCKETS = Object.freeze([90, 100, 110, 120, 130, 140, 150]);

// Map from canonical node type → default height. Types not listed default to 100.
// References: ORKES_DAG_RULESET §S-G (as captured in IR_RULE_MAPPING.md),
// cross-checked against the Conductor OSS live-research fixtures for heights.
export const TASK_TYPE_HEIGHTS = Object.freeze({
  // Scalar / leaf tasks
  simple: 100,
  http: 130,                  // URL + method line
  event: 130,                 // event sink line (conductor://, kafka://, sqs://)
  kafka_publish: 150,         // topic badge + bootstrap servers line
  inline: 140,                // two-line expression preview
  lambda: 140,                // two-line code preview
  json_jq_transform: 140,     // jq query preview
  wait: 110,                  // duration label
  set_variable: 110,          // variable name chip
  human: 130,                 // assignee / group
  user_task: 130,
  approval: 130,
  sub_workflow: 130,          // target workflow ref
  start_workflow: 130,
  async_sub_workflow: 130,
  dynamic_task: 120,          // resolved task ref line
  get_signed_jwt: 110,
  update_secret: 110,
  webhook: 120,               // endpoint preview
  business_rule: 130,         // rule expression preview

  // Container / control nodes — heights set by the IR's container layout,
  // NOT by this table. We list them with 100 so callers that pass every
  // node through this function get a safe default.
  switch: 100,
  fork_join: 100,
  fork_join_dynamic: 100,
  join: 100,
  parallel_join: 100,
  switch_join: 100,
  convergence: 100,
  do_while: 100,
  for_each: 100,
  loop: 100,

  // Display-only markers
  manual_start: 100,
  end: 100,
  // §121f Gap E: Conductor OSS renders TERMINATE at 350×90 (10px shorter than regular
  // tasks). The IR types already encode this (`TERMINATE_H = 90` in ir/types.js);
  // align the legacy anatomy height so both pipelines emit the same dimension.
  terminate: 90,
});

/**
 * Return the bucketed height for a node of the given type.
 *
 * The `config` argument is accepted so future refinements (e.g. KAFKA with
 * no topic set → drop to 130) can read task config without changing the
 * signature. For now we only use `nodeType`.
 *
 * @param {string} nodeType
 * @param {object} [config]
 * @returns {number} one of {100, 110, 120, 130, 140, 150}
 */
export function getTaskHeight(nodeType, /* config */ _config) {
  if (typeof nodeType !== 'string' || nodeType.length === 0) return 100;
  const h = TASK_TYPE_HEIGHTS[nodeType];
  if (typeof h !== 'number') return 100;
  // Defensive snap — if the table is ever edited with an off-bucket value,
  // clamp to the nearest legal bucket so layout stays well-behaved.
  return snapToBucket(h);
}

/**
 * Snap a raw pixel height to the nearest S-G bucket. Rounds UP to be safe
 * (truncating would risk the inner content clipping under the border).
 *
 * Special case: the value 90 passes through unchanged. It is the TERMINATE-
 * marker bucket (§121f Gap E, Conductor OSS parity). Raw heights below 90 or between
 * (90, 100] still snap up to 100 — only an exact 90 is treated as the
 * reserved TERMINATE bucket to keep downstream consumers stable.
 *
 * @param {number} px
 * @returns {number} one of {90, 100, 110, 120, 130, 140, 150}
 */
export function snapToBucket(px) {
  if (!Number.isFinite(px)) return 100;
  if (px === 90) return 90;
  if (px <= 100) return 100;
  if (px >= 150) return 150;
  // Round up to the next multiple of 10 within [100..150].
  return Math.min(150, Math.max(100, Math.ceil(px / 10) * 10));
}

// ────────────────────────────────────────────────────────────────
//  Metadata extraction — ORKES_DAG_RULESET §S-G.b
// ────────────────────────────────────────────────────────────────
//
// Each supported type returns a single metadata line to render below the
// sublabel. The `kind` drives CSS class selection in CanvasNode so each
// type can have a distinctive badge colour without hard-coding the string
// match in the renderer.

/**
 * Extract the per-type metadata line for a node, or null if the type has
 * no metadata to show (renderer treats null as "skip the third line").
 *
 * @param {string} nodeType
 * @param {object} [config]  Node.config from the canvas state
 * @returns {{line: string, kind: string} | null}
 */
export function getTaskMetadata(nodeType, config) {
  if (typeof nodeType !== 'string' || nodeType.length === 0) return null;
  const cfg = config && typeof config === 'object' ? config : {};

  switch (nodeType) {
    case 'http': {
      const method = (cfg.method || cfg.httpMethod || 'GET').toString().toUpperCase();
      const url = cfg.url || cfg.uri || cfg.endpoint || '';
      if (!url) return null;
      return { kind: 'http', line: `${method} ${truncate(url, 48)}` };
    }
    case 'event': {
      const sink = cfg.sink || cfg.eventSink || cfg.channel || '';
      if (!sink) return null;
      return { kind: 'event', line: truncate(sink, 56) };
    }
    case 'kafka_publish': {
      const topic = cfg.topic || cfg.kafkaTopic || '';
      if (!topic) return null;
      return { kind: 'kafka', line: `topic: ${truncate(topic, 48)}` };
    }
    case 'inline':
    case 'lambda': {
      const expr = cfg.expression || cfg.scriptExpression || cfg.code || '';
      if (!expr) return null;
      return { kind: 'code', line: truncate(firstLine(expr), 56) };
    }
    case 'json_jq_transform': {
      const q = cfg.query || cfg.queryExpression || '';
      if (!q) return null;
      return { kind: 'jq', line: truncate(firstLine(q), 56) };
    }
    case 'wait': {
      const dur = cfg.duration || cfg.until || cfg.waitFor || '';
      if (!dur) return null;
      return { kind: 'wait', line: String(dur) };
    }
    case 'set_variable': {
      const names = extractVariableNames(cfg);
      if (names.length === 0) return null;
      return { kind: 'var', line: names.join(', ') };
    }
    case 'human':
    case 'user_task':
    case 'approval': {
      const who = cfg.assignee || cfg.assignedTo || cfg.group || cfg.userGroup || '';
      if (!who) return null;
      return { kind: 'human', line: truncate(String(who), 48) };
    }
    case 'sub_workflow':
    case 'start_workflow':
    case 'async_sub_workflow': {
      const ref = cfg.subWorkflowName || cfg.workflowName || cfg.name || '';
      if (!ref) return null;
      const ver = cfg.subWorkflowVersion || cfg.version;
      return { kind: 'subflow', line: ver ? `${ref} v${ver}` : String(ref) };
    }
    case 'dynamic_task': {
      const ref = cfg.dynamicTaskName || cfg.taskRef || cfg.taskType || '';
      if (!ref) return null;
      return { kind: 'dynamic', line: truncate(String(ref), 48) };
    }
    case 'get_signed_jwt':
      return { kind: 'auth', line: 'signed JWT' };
    case 'update_secret':
      return { kind: 'auth', line: 'update secret' };
    case 'webhook': {
      const target = cfg.endpoint || cfg.url || cfg.callbackUrl || '';
      if (!target) return null;
      return { kind: 'webhook', line: truncate(target, 48) };
    }
    case 'business_rule': {
      const rule = cfg.ruleExpression || cfg.rule || '';
      if (!rule) return null;
      return { kind: 'rule', line: truncate(firstLine(rule), 56) };
    }
    default:
      return null;
  }
}

// ────────────────────────────────────────────────────────────────
//  Private helpers
// ────────────────────────────────────────────────────────────────

/**
 * Truncate a string to `n` chars, appending an ellipsis when cut. Never
 * returns undefined — falsy inputs become an empty string.
 * @param {string|null|undefined} s
 * @param {number} n
 */
function truncate(s, n) {
  if (typeof s !== 'string' || s.length === 0) return '';
  if (!Number.isFinite(n) || n <= 0) return s;
  if (s.length <= n) return s;
  return s.slice(0, n - 1) + '…';
}

/**
 * First non-empty line of a multi-line string, stripped of leading/trailing
 * whitespace. Empty string if none.
 * @param {string} s
 */
function firstLine(s) {
  if (typeof s !== 'string') return '';
  for (const line of s.split(/\r?\n/)) {
    const t = line.trim();
    if (t.length > 0) return t;
  }
  return '';
}

/**
 * Extract variable names from a SET_VARIABLE config. Supports both the
 * `variables: { name: value }` object form and an `assignments: [...]` list.
 * @param {object} cfg
 * @returns {string[]}
 */
function extractVariableNames(cfg) {
  const out = [];
  if (cfg && typeof cfg.variables === 'object' && cfg.variables !== null) {
    for (const k of Object.keys(cfg.variables)) out.push(k);
  }
  if (Array.isArray(cfg?.assignments)) {
    for (const a of cfg.assignments) {
      if (a && typeof a === 'object' && a.name) out.push(String(a.name));
    }
  }
  return out;
}

// ────────────────────────────────────────────────────────────────
//  Bulk helper — build the heightMap fed into irAdapter's first pass
// ────────────────────────────────────────────────────────────────

/**
 * Build an id → height map from a canvas node list using `getTaskHeight`.
 * Produces the same shape `useIRHeightFeedback` emits post-measurement, so
 * `layoutDAGviaIR(nodes, connections, { initialHeightMap })` can use it as
 * the seed for the first layout pass and skip the "every leaf is 100px"
 * default that causes container sizes to be wrong on first paint.
 *
 * @param {Array<{id: string, type: string, config?: object}>} nodes
 * @returns {Object.<string, number>}
 */
export function buildInitialHeightMap(nodes) {
  const out = {};
  if (!Array.isArray(nodes)) return out;
  for (const n of nodes) {
    if (!n || typeof n.id !== 'string') continue;
    out[n.id] = getTaskHeight(n.type || '', n.config);
  }
  return out;
}
